"use client";

import Link from "next/link";
import type { CSSProperties } from "react";
import { useEffect, useMemo, useState } from "react";
import { NextIntlClientProvider, useTranslations } from "next-intl";
import { useExceptionLogger } from "@/lib/observability/client";
import messagesEn from "../../messages/en.json";
import messagesEs from "../../messages/es.json";

type GlobalErrorProps = {
  readonly error: Error & { digest?: string };
  readonly reset: () => void;
};

const containerStyle: CSSProperties = {
  minHeight: "100vh",
  margin: 0,
  backgroundColor: "#000000",
  color: "#f8fafc",
};

const mainStyle: CSSProperties = {
  maxWidth: "34rem",
  margin: "0 auto",
  padding: "4.75rem 1.5rem",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "1.6rem",
  textAlign: "center",
};

const actionsStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  gap: "0.85rem",
};

const buttonStyle: CSSProperties = {
  padding: "0.65rem 1.5rem",
  borderRadius: "9999px",
  border: "none",
  backgroundColor: "var(--gruv-pink)",
  color: "var(--gruv-on-brand)",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: "0.95rem",
};

const secondaryLinkStyle: CSSProperties = {
  padding: "0.6rem 1.4rem",
  borderRadius: "9999px",
  border: "1px solid rgba(148, 163, 184, 0.35)",
  color: "#f8fafc",
  textDecoration: "none",
  fontWeight: 600,
  fontSize: "0.95rem",
};

const DEFAULT_LOCALE = "es";
const LOCALE_COOKIE = "NEXT_LOCALE";

const resolveLocaleFromCookie = (): "es" | "en" => {
  if (typeof document === "undefined") {
    return DEFAULT_LOCALE;
  }
  const match = document.cookie.match(new RegExp(`(?:^|; )${LOCALE_COOKIE}=([^;]*)`));
  const cookieLocale = match?.[1];
  return cookieLocale === "en" ? "en" : "es";
};

function GlobalErrorContent({ error, reset }: GlobalErrorProps) {
  const t = useTranslations("other");
  const digest = error.digest ?? null;
  useExceptionLogger(error, {
    boundary: "consumer-site/root",
    digest,
  });

  return (
    <main style={mainStyle}>
      <div>
        <h1 style={{ fontSize: "2.25rem", fontWeight: 600, marginBottom: "0.4rem" }}>
          {t("errorPage.title")}
        </h1>
        <p style={{ fontSize: "1rem", color: "#cbd5f5", margin: 0 }}>
          {t("errorPage.subtitlePrefix")} <code> [consumer-site] ui.exception</code>{" "}
          {t("errorPage.subtitleSuffix")}
        </p>
      </div>
      <div style={{ fontSize: "0.85rem", color: "#94a3b8" }}>
        {digest ? t("errorPage.digest", { digest }) : t("errorPage.noDigest")}
      </div>
      <div style={actionsStyle}>
        <button type="button" style={buttonStyle} onClick={reset}>
          {t("errorPage.retry")}
        </button>
        <Link href="/events" style={secondaryLinkStyle}>
          {t("errorPage.backToEvents")}
        </Link>
      </div>
    </main>
  );
}

export default function GlobalError(props: GlobalErrorProps) {
  const [locale, setLocale] = useState<"es" | "en">(DEFAULT_LOCALE);

  useEffect(() => {
    setLocale(resolveLocaleFromCookie());
  }, []);

  const messages = useMemo(() => (locale === "en" ? messagesEn : messagesEs), [locale]);

  return (
    <html lang={locale}>
      <body style={containerStyle}>
        <NextIntlClientProvider locale={locale} messages={messages}>
          <GlobalErrorContent {...props} />
        </NextIntlClientProvider>
      </body>
    </html>
  );
}
